import { Quaternion, Vector3 } from 'three'

import type { AnyID, LinkedItem, SceneObjects } from '@types'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import {
  isLateralOrCostadoLinkedCatalog,
  isRegletaPuertaLinkedCatalog,
} from '@utils/sceneObjects/linked/linkedTextureFields'

/**
 * Responsabilidad del costado en un extremo de fila.
 *
 * El agrupado (`getContiguousItemsForLaterals`) parte por familia para elegir
 * catálogo (LB / LA / CC). Eso no decide quién cubre una junta: un vecino de
 * otra familia se veía como extremo libre y cada unidad dibujaba por su cuenta.
 *
 * Aquí la fila sigue decidiendo *si* el lado está expuesto. Esta capa decide
 * *quién* dibuja cuando varias unidades comparten esa cara.
 *
 * Familia cubriente (hoy: COL): si participa en el lado, el costado es suyo.
 * El resto cede. Extensible: añadir tipos a `COVERING_FAMILIES`.
 */

export type CabinetFamily = 'MBJ' | 'MAL' | 'COL' | 'other'

export type RunSlotsMap = Map<AnyID, { left?: AnyID; right?: AnyID }>

/** Tipos que cubren el costado de una junta con otras familias. */
export const COVERING_FAMILIES: readonly CabinetFamily[] = ['COL']

const SIDE_CONTACT_GAP_MM = 60
const VERTICAL_OVERLAP_MM = 10

const tmpSelfPos = new Vector3()
const tmpSelfQuat = new Quaternion()
const tmpOtherPos = new Vector3()
const tmpOtherQuat = new Quaternion()
const tmpScale = new Vector3()
const tmpSelfX = new Vector3()
const tmpSelfZ = new Vector3()
const tmpOtherX = new Vector3()
const tmpOtherZ = new Vector3()
const tmpSelfEdge = new Vector3()
const tmpOtherLeft = new Vector3()
const tmpOtherRight = new Vector3()
const tmpSelfCenter = new Vector3()
const tmpOtherCenter = new Vector3()
const tmpDelta = new Vector3()

export function getCabinetFamily(
  item?: { tipoNormalizado?: string } | null,
): CabinetFamily {
  const type = String(item?.tipoNormalizado ?? '').toUpperCase()
  if (type === 'MBJ' || type === 'MAL' || type === 'COL') return type
  return 'other'
}

export function isCoveringFamily(family: CabinetFamily): boolean {
  return COVERING_FAMILIES.includes(family)
}

/** Mueble de composición (no costado, frontal ni regleta). */
export function isCabinetFurniture(item?: SceneObjects | null): item is LinkedItem {
  if (!item || !isLinkedItem(item) || !item.visible) return false
  if ((item.catalogID?.toString().toLowerCase() ?? '').includes('frontal')) return false
  if (isLateralOrCostadoLinkedCatalog(item)) return false
  if (isRegletaPuertaLinkedCatalog(item)) return false
  return true
}

export function getRunOccupantId(
  item: { id: AnyID; snappedSides?: { left?: AnyID; right?: AnyID } },
  isLeftSide: boolean,
  runSlots?: RunSlotsMap,
): AnyID | undefined {
  const fromSlots = runSlots?.get(item.id)?.[isLeftSide ? 'left' : 'right']
  if (fromSlots) return fromSlots
  return item.snappedSides?.[isLeftSide ? 'left' : 'right']
}

function familyMatches(
  family: CabinetFamily,
  wanted: CabinetFamily | readonly CabinetFamily[],
): boolean {
  if (typeof wanted === 'string') return family === wanted
  return wanted.includes(family)
}

function xzDistance(a: Vector3, b: Vector3): number {
  return Math.hypot(a.x - b.x, a.z - b.z)
}

/**
 * Contacto a lo ancho (left/right locales). El registro de caras solo guarda un
 * ocupante: si MAL y MBJ están apilados contra la misma cara de una COL, el
 * segundo se detecta aquí.
 */
export function itemsTouchOnWidthSide(
  self: LinkedItem,
  other: LinkedItem,
  isLeftSide: boolean,
): boolean {
  if (self.id === other.id || !self.matrix || !other.matrix) return false

  self.matrix.decompose(tmpSelfPos, tmpSelfQuat, tmpScale)
  other.matrix.decompose(tmpOtherPos, tmpOtherQuat, tmpScale)

  const selfTop = tmpSelfPos.y + (self.height || 0)
  const otherTop = tmpOtherPos.y + (other.height || 0)
  if (Math.min(selfTop, otherTop) - Math.max(tmpSelfPos.y, tmpOtherPos.y) <= VERTICAL_OVERLAP_MM) {
    return false
  }

  tmpSelfX.set(1, 0, 0).applyQuaternion(tmpSelfQuat)
  tmpSelfZ.set(0, 0, 1).applyQuaternion(tmpSelfQuat)
  tmpOtherX.set(1, 0, 0).applyQuaternion(tmpOtherQuat)
  tmpOtherZ.set(0, 0, 1).applyQuaternion(tmpOtherQuat)

  tmpSelfEdge.copy(tmpSelfPos)
  if (!isLeftSide) tmpSelfEdge.addScaledVector(tmpSelfX, self.width || 0)

  tmpOtherLeft.copy(tmpOtherPos)
  tmpOtherRight.copy(tmpOtherPos).addScaledVector(tmpOtherX, other.width || 0)
  const gap = Math.min(xzDistance(tmpSelfEdge, tmpOtherLeft), xzDistance(tmpSelfEdge, tmpOtherRight))
  if (gap > SIDE_CONTACT_GAP_MM) return false

  tmpSelfCenter
    .copy(tmpSelfPos)
    .addScaledVector(tmpSelfX, (self.width || 0) / 2)
    .addScaledVector(tmpSelfZ, (self.thickness || 0) / 2)
  tmpOtherCenter
    .copy(tmpOtherPos)
    .addScaledVector(tmpOtherX, (other.width || 0) / 2)
    .addScaledVector(tmpOtherZ, (other.thickness || 0) / 2)
  tmpDelta.subVectors(tmpOtherCenter, tmpSelfCenter)
  const alongDepth = Math.abs(tmpDelta.dot(tmpSelfZ))
  const maxDepth = ((self.thickness || 0) + (other.thickness || 0)) / 2 + SIDE_CONTACT_GAP_MM
  return alongDepth <= maxDepth
}

export function findAdjacentCabinetOnSide(args: {
  item: LinkedItem
  isLeftSide: boolean
  items: Map<AnyID, SceneObjects>
  runSlots?: RunSlotsMap
  family: CabinetFamily | readonly CabinetFamily[]
}): LinkedItem | null {
  const { item, isLeftSide, items, runSlots, family } = args

  const occupantId = getRunOccupantId(item, isLeftSide, runSlots)
  const occupant = occupantId ? items.get(occupantId) : undefined
  if (isCabinetFurniture(occupant) && familyMatches(getCabinetFamily(occupant), family)) {
    return occupant
  }

  for (const other of items.values()) {
    if (!isCabinetFurniture(other) || other.id === item.id) continue
    if (!familyMatches(getCabinetFamily(other), family)) continue
    if (itemsTouchOnWidthSide(item, other, isLeftSide)) return other
  }

  return null
}

export function isRunSideFree(
  item: LinkedItem,
  isLeftSide: boolean,
  groupIds: Set<string> | undefined,
  runSlots: RunSlotsMap,
  items: Map<AnyID, SceneObjects>,
): boolean {
  const occupant = runSlots.get(item.id)?.[isLeftSide ? 'left' : 'right']
  if (!occupant) return true
  if (!items.get(occupant)) return true
  if (groupIds && !groupIds.has(String(occupant))) return true
  return false
}

/**
 * True si este mueble debe llevar el costado de ese lado.
 *
 * 1. Si una familia cubriente está en el lado y este mueble no lo es → cede.
 * 2. Si este mueble es cubriente y el vecino no → dibuja (aunque compartan grupo).
 * 3. Si no, extremo libre de fila / vecino fuera del subgrupo.
 */
export function shouldHostLateral(args: {
  item: LinkedItem
  isLeftSide: boolean
  groupIds?: Set<string>
  runSlots: RunSlotsMap
  items: Map<AnyID, SceneObjects>
}): boolean {
  const { item, isLeftSide, groupIds, runSlots, items } = args
  const family = getCabinetFamily(item)

  const coveringNeighbor = findAdjacentCabinetOnSide({
    item,
    isLeftSide,
    items,
    runSlots,
    family: COVERING_FAMILIES,
  })
  if (coveringNeighbor && !isCoveringFamily(family)) return false

  if (isCoveringFamily(family)) {
    const coveredNeighbor = findAdjacentCabinetOnSide({
      item,
      isLeftSide,
      items,
      runSlots,
      family: ['MBJ', 'MAL'],
    })
    if (coveredNeighbor) return true
  }

  return isRunSideFree(item, isLeftSide, groupIds, runSlots, items)
}
